import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { BarChart } from '@/components/Chart/BarChart';
import { BarList } from '@/components/Chart/BarList';
import { DonutChart } from '@/components/Chart/DonutChart';
import {
    SegmentedControl,
    SegmentedControlItem,
} from '@/components/SegmentedControl/SegmentedControl';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import {
    type CountRow,
    type RankedRow,
    type ReportOverview,
    type SalesBucket,
} from '@/features/reports/api';
import { REPORTS_COPY as COPY } from '@/features/reports/copy';
import { formatPercent, formatPounds, formatPoundsCompact } from '@/features/reports/format';
import { PERIOD_AXIS, type ReportPeriod } from '@/features/reports/periods';
import { KpiTile, PeriodControl, ReportCard, ReportFailure, Tile } from '@/features/reports/parts';
import { reportOverviewQuery } from '@/features/reports/queries';
import { revealChoice } from '@/features/reports/reveal';

/**
 * How much of a bar's refunds its stack draws, so cost, refunds and gross profit stand exactly as
 * tall as the revenue. Refunds larger than the margin leave the gross profit negative, which the
 * chart cannot draw below the axis; that loss comes off the refunds segment instead, and the
 * negative margin above the bar says so. A period with refunds and no sales therefore draws no
 * bar; its refunds stay in the chart's hidden table and in Transactions › Margin.
 */
function drawnRefunds(bucket: SalesBucket): number {
    return Math.max(0, bucket.refunds + Math.min(0, bucket.grossProfit));
}

/**
 * The Reports Overview tab (ALS-85), built from screens/reports.html's Overview tab and the ticket's
 * metric list; the page and its tabs are ReportsPage's. One request fills the whole tab, so loading
 * and failure are the tab's and never a card's: half of it arriving is not a state it can be in. Every figure is the API's,
 * formatted here and never added up, so it cannot disagree with the dashboard's Sales card. Gross
 * profit and margin are after the refunds settled in the period (ALS-411), so they match
 * Transactions › Margin; the chart stacks cost, refunds and gross profit up to revenue.
 */
export function OverviewTab({
    period,
    onPeriodChange,
}: {
    period: ReportPeriod;
    onPeriodChange: (period: ReportPeriod) => void;
}) {
    const overview = useQuery(reportOverviewQuery(period));
    const data = overview.data;

    return (
        <>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <PeriodControl period={period} onPeriodChange={onPeriodChange} />
                {data ? (
                    <span className="text-muted-foreground text-xs">
                        {COPY.hint(data.current.label, data.previous.label)}
                    </span>
                ) : null}
            </div>

            {overview.isPending ? (
                <OverviewSkeleton />
            ) : overview.isError && !data ? (
                <ReportFailure copy={COPY.failed} onRetry={() => void overview.refetch()} />
            ) : data ? (
                <OverviewBody data={data} period={period} />
            ) : null}
        </>
    );
}

function OverviewBody({ data, period }: { data: ReportOverview; period: ReportPeriod }) {
    const { featured, kpis, mix } = data;
    const window = data.current.label;
    const statusTotal = mix.statuses.reduce((sum, row) => sum + row.count, 0);

    return (
        <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Tile
                    label={COPY.featured.week}
                    value={formatPounds(featured.week.revenue)}
                    note={COPY.featured.orders(featured.week.orders)}
                />
                <Tile
                    label={COPY.featured.month}
                    value={formatPounds(featured.month.revenue)}
                    note={COPY.featured.orders(featured.month.orders)}
                />
                <Tile
                    label={COPY.featured.year}
                    value={formatPounds(featured.year.revenue)}
                    note={COPY.featured.orders(featured.year.orders)}
                />
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <KpiTile
                    label={COPY.kpis.revenue}
                    metric={kpis.revenue}
                    format={formatPoundsCompact}
                />
                <KpiTile
                    label={COPY.kpis.grossProfit}
                    metric={kpis.grossProfit}
                    format={formatPoundsCompact}
                    note={COPY.kpis.afterRefunds}
                />
                <KpiTile
                    label={COPY.kpis.marginPercentage}
                    metric={kpis.marginPercentage}
                    format={formatPercent}
                    unit="pt"
                    note={COPY.kpis.afterRefunds}
                />
                <KpiTile label={COPY.kpis.orders} metric={kpis.orders} format={String} />
                <KpiTile
                    label={COPY.kpis.averageOrderValue}
                    metric={kpis.averageOrderValue}
                    format={formatPoundsCompact}
                />
                <KpiTile label={COPY.kpis.units} metric={kpis.units} format={String} />
            </div>

            <div className="grid min-w-0 gap-(--layout-row-gap) lg:grid-cols-[3fr_2fr]">
                <ReportCard
                    title={COPY.revenue.title}
                    subtitle={COPY.revenue.subtitle(PERIOD_AXIS[period])}
                >
                    <BarChart
                        title={COPY.revenue.title}
                        labels={data.series.map((b) => b.label)}
                        series={[
                            {
                                label: COPY.revenue.cost,
                                values: data.series.map((b) => b.cost),
                                colour: 5,
                            },
                            {
                                label: COPY.revenue.refunds,
                                values: data.series.map((b) => b.refunds),
                                drawn: data.series.map(drawnRefunds),
                                colour: 3,
                            },
                            {
                                label: COPY.revenue.grossProfit,
                                values: data.series.map((b) => b.grossProfit),
                                colour: 0,
                            },
                        ]}
                        stacked
                        topLabels={data.series.map((b) =>
                            b.marginPercentage === null ? null : formatPercent(b.marginPercentage),
                        )}
                        format={formatPoundsCompact}
                    />
                </ReportCard>
                <TopPerformersCard top={data.top} window={window} />
            </div>

            <div className="grid min-w-0 gap-(--layout-row-gap) lg:grid-cols-3">
                <ReportCard
                    title={COPY.occasions.title}
                    subtitle={`${COPY.occasions.subtitle} · ${window}`}
                >
                    <DonutChart
                        title={COPY.occasions.title}
                        items={counts(mix.occasions)}
                        format={COPY.orders}
                        emptyNote={COPY.emptyOrders}
                    />
                </ReportCard>
                <ReportCard
                    title={COPY.channels.title}
                    subtitle={`${COPY.channels.subtitle} · ${window}`}
                >
                    <BarList
                        items={counts(mix.channels)}
                        format={String}
                        colour={1}
                        emptyNote={COPY.emptyOrders}
                    />
                </ReportCard>
                <ReportCard
                    title={COPY.types.title}
                    subtitle={`${COPY.types.subtitle} · ${window}`}
                >
                    <BarList
                        items={counts(mix.types)}
                        format={String}
                        colour={1}
                        emptyNote={COPY.emptyOrders}
                    />
                </ReportCard>
            </div>

            <div className="grid min-w-0 gap-(--layout-row-gap) lg:grid-cols-2">
                <ReportCard
                    title={COPY.statuses.title}
                    subtitle={COPY.statuses.subtitle(statusTotal)}
                >
                    {mix.statuses.length === 0 ? (
                        <p className="text-muted-foreground py-4 text-sm">{COPY.emptyOrders}</p>
                    ) : (
                        // Each tile carries its share: 32 of 65 is the story, and "32" alone is not.
                        <div className="grid grid-cols-2 gap-3">
                            {mix.statuses.map((s) => (
                                <div
                                    key={s.name}
                                    className="bg-muted border-border rounded-md border px-4 py-3"
                                >
                                    <p className="text-[22px] font-semibold">
                                        {s.count}
                                        <span className="text-muted-foreground text-[13px] font-medium">
                                            {' · '}
                                            {Math.round((s.count / statusTotal) * 100)}%
                                        </span>
                                    </p>
                                    <p className="text-label-md text-muted-foreground mt-0.5">
                                        {s.name}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </ReportCard>
                <ReportCard
                    title={COPY.regions.title}
                    subtitle={`${COPY.regions.subtitle} · ${window}`}
                >
                    <BarList
                        items={ranked(data.regions)}
                        format={formatPoundsCompact}
                        emptyNote={COPY.top.empty}
                    />
                </ReportCard>
            </div>
        </>
    );
}

const TOP_TABS = ['clients', 'brands', 'suppliers', 'categories'] as const;
type TopTab = (typeof TOP_TABS)[number];

/** One ranking at a time, switched in place: the four lists arrived with the page, so no request. */
function TopPerformersCard({ top, window }: { top: ReportOverview['top']; window: string }) {
    const [tab, setTab] = useState<TopTab>('clients');
    return (
        <ReportCard title={COPY.top.title} subtitle={COPY.top.subtitle(window)}>
            <SegmentedControl
                tone="solid"
                aria-label={COPY.top.title}
                value={tab}
                onValueChange={(next) => setTab(next as TopTab)}
                // Four choices are wider than a 320 phone's card: the row scrolls rather than
                // pushing the last one out of it.
                className="mb-3 max-w-full overflow-x-auto"
            >
                {TOP_TABS.map((key) => (
                    <SegmentedControlItem key={key} value={key} onClick={revealChoice}>
                        {COPY.top.tabs[key]}
                    </SegmentedControlItem>
                ))}
            </SegmentedControl>
            {tab !== 'clients' ? (
                <p className="text-muted-foreground mb-1 text-xs">{COPY.top.lineNote}</p>
            ) : null}
            <BarList
                ranked
                items={ranked(top[tab])}
                format={formatPoundsCompact}
                emptyNote={COPY.top.empty}
            />
        </ReportCard>
    );
}

/** The page's whole body, waiting: one skeleton for the page, never one per card. */
function OverviewSkeleton() {
    return (
        <div
            aria-busy="true"
            aria-label="Loading the report"
            className="flex flex-col gap-(--layout-row-gap)"
        >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {Array.from({ length: 3 }, (_, i) => (
                    <Skeleton key={i} className="h-[84px] rounded-lg" />
                ))}
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                {Array.from({ length: 6 }, (_, i) => (
                    <Skeleton key={i} className="h-[84px] rounded-lg" />
                ))}
            </div>
            <div className="grid gap-(--layout-row-gap) lg:grid-cols-[3fr_2fr]">
                <Skeleton className="h-[320px] rounded-md" />
                <Skeleton className="h-[320px] rounded-md" />
            </div>
        </div>
    );
}

const counts = (rows: CountRow[]) => rows.map((r) => ({ label: r.name, value: r.count }));
const ranked = (rows: RankedRow[]) => rows.map((r) => ({ label: r.name, value: r.value }));
